/**
 * Row reads shared by several commands (`rows`, `tree`, `patch`, `undo`,
 * `watch`): one row or null, and every page of a listing.
 */
import type { Row } from '@acp/domain/row.js';
import { allPages, ApiError, type ApiClient } from './client.js';

/**
 * The row, or null when the rundown has no such row. A rundown that is itself
 * not found (absent, or another tenant's) is NOT a missing row: its
 * `not-found` is thrown, so a command reports the contract's error instead of
 * suggesting the row be created (found by the phase-1 e2e run).
 */
export async function getRowOrNull(api: ApiClient, rundownId: string, rowId: string): Promise<Row | null> {
  try {
    return (await api.call('getRow', { params: { rundownId, rowId }, query: null, body: null })).row;
  } catch (err) {
    if (!(err instanceof ApiError && err.code === 'not-found')) throw err;
  }
  // getRow answers not-found for both; getRundown tells them apart (and throws its own not-found).
  await api.call('getRundown', { params: { rundownId }, query: null, body: null });
  return null;
}

export type RowFilter = { readonly parentId?: string; readonly changedSince?: string; readonly limit?: string };

/** Every row matching `filter`, all pages, and the cursor of the first page (the listing's high-water mark). */
export async function listAllRows(api: ApiClient, rundownId: string, filter: RowFilter): Promise<{ rows: Row[]; cursor: string | null }> {
  let cursor: string | null = null;
  const rows = await allPages(async (pageToken) => {
    const page = await api.call('listRows', {
      params: { rundownId },
      query: { ...filter, ...(pageToken === undefined ? {} : { pageToken }) },
      body: null,
    });
    cursor ??= page.cursor;
    return { items: page.rows, nextPageToken: page.nextPageToken };
  });
  return { rows, cursor };
}
